import { useAuth } from "@clerk/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Fragment, useMemo, useState, type ReactElement } from "react";
import { ChevronDown, ChevronRight, RefreshCw } from "lucide-react";
import {
  toast,
  Button,
  Input,
  Label,
  Textarea,
  RadioGroup,
  RadioGroupItem,
} from "@ndd/ui";
import { ApiRequestError, authenticatedApi } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/badge";
import { RdCombobox } from "@/components/v2/migrate-rd-combobox";
import { AdminGuard, AdminNav } from "./shared";

type MigrationState = "pending" | "in_progress" | "done";

interface MigrationRow {
  oldRunId: string;
  state: MigrationState;
  migratedAt: string;
  rdName: string;
  clientName: string;
  createdAt: string;
  sessionType: "initial" | "follow_up" | null;
  importedCount: number;
}

interface ExistingRd {
  id: string;
  name: string;
}

interface FormState {
  rdId: string | null;
  clientName: string;
  sessionDate: string;
  sessionType: "initial" | "follow_up" | null;
  transcript: string;
}

interface ImportedEntry {
  newSessionId: string;
  rdId: string;
  oldRunId: string;
  clientName: string;
  at: number;
}

function apiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiRequestError) {
    const body = error.body as { error?: string } | undefined;
    if (body?.error) return body.error;
  }

  return error instanceof Error ? error.message : fallback;
}

function initialFormFor(row: MigrationRow, existingRds: ExistingRd[]): FormState {
  const matchingRd = existingRds.find(
    (rd) => rd.name.toLowerCase() === row.rdName.toLowerCase()
  );

  return {
    rdId: matchingRd?.id ?? null,
    clientName: row.clientName,
    sessionDate: row.createdAt.slice(0, 10),
    sessionType: row.sessionType,
    transcript: "",
  };
}

export function AdminMigrate(): ReactElement {
  return (
    <AdminGuard>
      <MigrateClient />
    </AdminGuard>
  );
}

type GetToken = Parameters<typeof authenticatedApi>[0];
type QueryClient = ReturnType<typeof useQueryClient>;
type UpdateForm = (oldRunId: string, patch: Partial<FormState>) => void;

function useMigrationData(getToken: GetToken) {
  const migrations = useQuery({
    queryKey: ["migrations"],
    queryFn: () =>
      authenticatedApi<{ data: MigrationRow[] }>(getToken, "/api/admin/migrate"),
  });
  const rdsQuery = useQuery({
    queryKey: ["migration-rds"],
    queryFn: () => authenticatedApi<{ data: ExistingRd[] }>(getToken, "/api/rds"),
  });

  const rows = useMemo(() => migrations.data?.data ?? [], [migrations.data]);
  const existingRds = useMemo(
    () => (rdsQuery.data?.data ?? []).map((rd) => ({ id: rd.id, name: rd.name })),
    [rdsQuery.data]
  );

  return { migrations, rows, existingRds };
}

function useMigrationForms(rows: MigrationRow[], existingRds: ExistingRd[]) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [forms, setForms] = useState<Record<string, FormState>>({});

  function getForm(row: MigrationRow): FormState {
    return forms[row.oldRunId] ?? initialFormFor(row, existingRds);
  }

  function updateForm(oldRunId: string, patch: Partial<FormState>) {
    setForms((prev) => {
      const base =
        prev[oldRunId] ??
        initialFormFor(rows.find((r) => r.oldRunId === oldRunId)!, existingRds);

      return { ...prev, [oldRunId]: { ...base, ...patch } };
    });
  }

  function toggleExpanded(oldRunId: string) {
    setExpandedId((prev) => (prev === oldRunId ? null : oldRunId));
    setForms((prev) => {
      if (prev[oldRunId]) return prev;

      const row = rows.find((r) => r.oldRunId === oldRunId);
      if (!row) return prev;

      return { ...prev, [oldRunId]: initialFormFor(row, existingRds) };
    });
  }

  return { expandedId, setExpandedId, getForm, updateForm, toggleExpanded };
}

function useSyncAction(getToken: GetToken, queryClient: QueryClient) {
  const [busySync, setBusySync] = useState(false);

  async function handleSync() {
    setBusySync(true);

    try {
      const result = await authenticatedApi<{ data: { inserted: number } }>(
        getToken,
        "/api/admin/migrate/sync",
        { method: "POST" }
      );
      const inserted = result.data.inserted;
      toast.success(
        inserted === 0
          ? "No new v1 sessions"
          : `Added ${inserted} new v1 ${inserted === 1 ? "session" : "sessions"}`
      );
      await queryClient.invalidateQueries({ queryKey: ["migrations"] });
    } catch (error) {
      toast.error(apiErrorMessage(error, "Sync failed"));
    } finally {
      setBusySync(false);
    }
  }

  return { busySync, handleSync };
}

function importFormError(form: FormState): string | null {
  if (!form.rdId) return "Select an RD before importing.";

  if (form.transcript.trim().length < 20) return "Transcript is too short.";

  return null;
}

function postImport(getToken: GetToken, row: MigrationRow, form: FormState) {
  return authenticatedApi<{
    data: { newSessionId: string; rdId: string };
  }>(getToken, "/api/admin/migrate/import", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      rdId: form.rdId,
      clientName: form.clientName,
      sessionDate: form.sessionDate,
      sessionType: form.sessionType,
      transcript: form.transcript,
      oldRunId: row.oldRunId,
    }),
  });
}

function invalidateAfterImport(queryClient: QueryClient, rdId: string) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ["migrations"] }),
    queryClient.invalidateQueries({ queryKey: ["rd", rdId] }),
    queryClient.invalidateQueries({ queryKey: ["rd-sessions", rdId] }),
  ]);
}

function useImportAction(
  getToken: GetToken,
  queryClient: QueryClient,
  forms: { getForm: (row: MigrationRow) => FormState; updateForm: UpdateForm }
) {
  const [busyImport, setBusyImport] = useState<string | null>(null);
  const [recentImports, setRecentImports] = useState<ImportedEntry[]>([]);

  async function handleImport(row: MigrationRow) {
    const form = forms.getForm(row);
    const formError = importFormError(form);

    if (formError) {
      toast.error(formError);

      return;
    }

    setBusyImport(row.oldRunId);

    try {
      const result = await postImport(getToken, row, form);
      forms.updateForm(row.oldRunId, { transcript: "" });
      setRecentImports((prev) => [
        {
          newSessionId: result.data.newSessionId,
          rdId: result.data.rdId,
          oldRunId: row.oldRunId,
          clientName: form.clientName || row.oldRunId,
          at: Date.now(),
        },
        ...prev,
      ]);
      toast.success(`Imported ${form.clientName || row.oldRunId}`);
      await invalidateAfterImport(queryClient, result.data.rdId);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Import failed"));
    } finally {
      setBusyImport(null);
    }
  }

  return { busyImport, recentImports, handleImport };
}

function stateChangeMessage(state: MigrationState): string {
  if (state === "done") return "Marked done";

  return state === "in_progress"
    ? "Moved back to in-progress"
    : "Moved back to pending";
}

function useSetStateAction(
  getToken: GetToken,
  queryClient: QueryClient,
  setExpandedId: (update: (prev: string | null) => string | null) => void
) {
  const [busyState, setBusyState] = useState<string | null>(null);

  async function handleSetState(oldRunId: string, state: MigrationState) {
    setBusyState(oldRunId);

    try {
      await authenticatedApi(getToken, `/api/admin/migrate/${oldRunId}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ state }),
      });
      toast.success(stateChangeMessage(state));

      if (state === "done") {
        setExpandedId((prev) => (prev === oldRunId ? null : prev));
      }

      await queryClient.invalidateQueries({ queryKey: ["migrations"] });
    } catch (error) {
      toast.error(apiErrorMessage(error, "Update failed"));
    } finally {
      setBusyState(null);
    }
  }

  return { busyState, handleSetState };
}

function migrationStats(rows: MigrationRow[]) {
  const counts = { pending: 0, in_progress: 0, done: 0 };
  for (const r of rows) counts[r.state] += 1;

  return counts;
}

function MigrateClient(): ReactElement {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const { migrations, rows, existingRds } = useMigrationData(getToken);
  const forms = useMigrationForms(rows, existingRds);
  const sync = useSyncAction(getToken, queryClient);
  const imports = useImportAction(getToken, queryClient, forms);
  const states = useSetStateAction(getToken, queryClient, forms.setExpandedId);
  const stats = useMemo(() => migrationStats(rows), [rows]);

  return (
    <div className="min-h-screen bg-background py-6 px-4">
      <div className="max-w-5xl mx-auto">
        <AdminNav />
        <MigrateHeader busySync={sync.busySync} onSync={() => void sync.handleSync()} />
        <MigrationStatsLine total={rows.length} stats={stats} />
        <MigrationTableArea
          migrations={migrations}
          total={rows.length}
          table={
            <TrackerTable
              rows={rows}
              existingRds={existingRds}
              expandedId={forms.expandedId}
              busyImport={imports.busyImport}
              busyState={states.busyState}
              onToggleExpanded={forms.toggleExpanded}
              onUpdateForm={forms.updateForm}
              onImport={(row) => void imports.handleImport(row)}
              onSetState={(oldRunId, state) =>
                void states.handleSetState(oldRunId, state)
              }
              getForm={forms.getForm}
            />
          }
        />
        <RecentImports entries={imports.recentImports} />
      </div>
    </div>
  );
}

function MigrateHeader({
  busySync,
  onSync,
}: {
  busySync: boolean;
  onSync: () => void;
}): ReactElement {
  return (
    <header className="mb-6 flex items-start justify-between gap-4">
      <div>
        <h1 className="text-lg font-semibold tracking-tight text-foreground font-sans">
          Migration Tracker
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Import v1 feedback runs into v2 as sessions. One v1 run can become multiple v2
          sessions.
        </p>
      </div>
      <Button
        onClick={onSync}
        disabled={busySync}
        className="bg-interactive text-white hover:bg-interactive/90"
      >
        <RefreshCw className={cn("mr-2 h-4 w-4", busySync && "animate-spin")} />
        {busySync ? "Syncing..." : "Sync from v1"}
      </Button>
    </header>
  );
}

function MigrationStatsLine({
  total,
  stats,
}: {
  total: number;
  stats: Record<MigrationState, number>;
}): ReactElement {
  return (
    <div className="flex items-center gap-3 mb-4 text-sm text-muted-foreground">
      <Badge variant="secondary">{total} total</Badge>
      <span>·</span>
      <span>{stats.pending} pending</span>
      <span>·</span>
      <span>{stats.in_progress} in progress</span>
      <span>·</span>
      <span>{stats.done} done</span>
    </div>
  );
}

function MigrationTableArea({
  migrations,
  total,
  table,
}: {
  migrations: { isLoading: boolean; isError: boolean; error: unknown };
  total: number;
  table: ReactElement;
}): ReactElement {
  if (migrations.isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-foreground/20"></div>
      </div>
    );
  }

  if (migrations.isError) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-2 text-sm text-destructive">
        {apiErrorMessage(migrations.error, "Failed to load migrations")}
      </div>
    );
  }

  if (total === 0) {
    return (
      <div className="rounded-lg border border-border bg-card py-12 text-center">
        <p className="text-muted-foreground">
          No v1 sessions tracked yet. Click &ldquo;Sync from v1&rdquo; to pull in
          current runs.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      {table}
    </div>
  );
}

function RecentImports({ entries }: { entries: ImportedEntry[] }): ReactElement | null {
  if (entries.length === 0) return null;

  return (
    <section className="mt-8">
      <h2 className="text-sm font-semibold text-foreground mb-2">
        Imported this session
      </h2>
      <ul className="space-y-1 text-sm">
        {entries.map((e) => (
          <li key={e.newSessionId} className="text-muted-foreground">
            <span className="font-mono text-xs">{e.oldRunId}</span>
            {" → "}
            <Link
              to="/rds/$rdId"
              params={{ rdId: e.rdId }}
              className="text-primary hover:underline"
            >
              {e.clientName || "session"}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

interface TrackerTableProps {
  rows: MigrationRow[];
  existingRds: ExistingRd[];
  expandedId: string | null;
  busyImport: string | null;
  busyState: string | null;
  onToggleExpanded: (oldRunId: string) => void;
  onUpdateForm: UpdateForm;
  onImport: (row: MigrationRow) => void;
  onSetState: (oldRunId: string, state: MigrationState) => void;
  getForm: (row: MigrationRow) => FormState;
}

function TrackerTable(props: TrackerTableProps): ReactElement {
  return (
    <table className="w-full">
      <thead className="bg-muted/50 border-b border-border">
        <tr className="text-left text-xs font-bold tracking-wider uppercase text-muted-foreground">
          <th className="w-8 py-2 px-3"></th>
          <th className="py-2 px-3">v1 ID</th>
          <th className="py-2 px-3">RD</th>
          <th className="py-2 px-3">Created</th>
          <th className="py-2 px-3">Type</th>
          <th className="py-2 px-3">Imported</th>
          <th className="py-2 px-3">State</th>
        </tr>
      </thead>
      <tbody>
        {props.rows.map((row) => (
          <TrackerRow key={row.oldRunId} row={row} table={props} />
        ))}
      </tbody>
    </table>
  );
}

function TrackerRow({
  row,
  table,
}: {
  row: MigrationRow;
  table: TrackerTableProps;
}): ReactElement {
  const isExpanded = table.expandedId === row.oldRunId;

  return (
    <Fragment>
      <TrackerSummaryRow
        row={row}
        isExpanded={isExpanded}
        onToggle={() => table.onToggleExpanded(row.oldRunId)}
      />
      {isExpanded && (
        <tr className="bg-muted/20">
          <td colSpan={7} className="p-4">
            <RowEditor
              row={row}
              existingRds={table.existingRds}
              form={table.getForm(row)}
              busyImport={table.busyImport === row.oldRunId}
              busyState={table.busyState === row.oldRunId}
              onUpdateForm={(patch) => table.onUpdateForm(row.oldRunId, patch)}
              onImport={() => table.onImport(row)}
              onSetState={(state) => table.onSetState(row.oldRunId, state)}
            />
          </td>
        </tr>
      )}
    </Fragment>
  );
}

function TrackerSummaryRow({
  row,
  isExpanded,
  onToggle,
}: {
  row: MigrationRow;
  isExpanded: boolean;
  onToggle: () => void;
}): ReactElement {
  return (
    <tr
      className={cn(
        "border-b border-border last:border-b-0 hover:bg-accent/30 cursor-pointer transition-colors",
        isExpanded && "bg-accent/20"
      )}
      onClick={onToggle}
    >
      <td className="py-2 px-3">
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
      </td>
      <td className="py-2 px-3 font-mono text-xs text-muted-foreground">
        {row.oldRunId}
      </td>
      <td className="py-2 px-3 text-sm">{row.rdName}</td>
      <td className="py-2 px-3 text-sm text-muted-foreground">
        {row.createdAt.slice(0, 10)}
      </td>
      <td className="py-2 px-3">
        {row.sessionType ? (
          <Badge variant="category">
            {row.sessionType === "initial" ? "Initial" : "Follow-up"}
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </td>
      <td className="py-2 px-3 text-sm tabular-nums">{row.importedCount}</td>
      <td className="py-2 px-3">
        <StateBadge state={row.state} />
      </td>
    </tr>
  );
}

function StateBadge({ state }: { state: MigrationState }): ReactElement {
  if (state === "done") return <Badge variant="default">Done</Badge>;

  if (state === "in_progress") return <Badge variant="analyzing">In progress</Badge>;

  return <Badge variant="secondary">Pending</Badge>;
}

interface RowEditorProps {
  row: MigrationRow;
  existingRds: ExistingRd[];
  form: FormState;
  busyImport: boolean;
  busyState: boolean;
  onUpdateForm: (patch: Partial<FormState>) => void;
  onImport: () => void;
  onSetState: (state: MigrationState) => void;
}

const FIELD_LABEL_CLASS =
  "text-xs font-bold tracking-wider uppercase text-muted-foreground";

function RowEditor(props: RowEditorProps): ReactElement {
  const { row, busyImport, busyState, onImport, onSetState } = props;
  const disabled = busyImport || busyState;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        {/* The legacy "Open v1 view" link pointed at the retired v1 app. */}
        <span className="font-mono text-xs text-muted-foreground">{row.oldRunId}</span>
        <RowStateActions
          state={row.state}
          disabled={disabled}
          busyState={busyState}
          onSetState={onSetState}
        />
      </div>

      <RowEditorFields {...props} disabled={disabled} />

      <div className="flex flex-col gap-1.5">
        <Label className={FIELD_LABEL_CLASS}>Transcript</Label>
        <Textarea
          value={props.form.transcript}
          onChange={(e) => props.onUpdateForm({ transcript: e.target.value })}
          placeholder="Paste one transcript here."
          disabled={disabled}
          rows={8}
          className="rounded-lg border-border font-mono text-xs"
        />
      </div>

      <div className="flex items-center justify-end">
        <Button
          onClick={onImport}
          disabled={disabled}
          className="bg-interactive text-white hover:bg-interactive/90"
        >
          {busyImport ? "Importing..." : "Import"}
        </Button>
      </div>
    </div>
  );
}

function RowStateActions({
  state,
  disabled,
  busyState,
  onSetState,
}: {
  state: MigrationState;
  disabled: boolean;
  busyState: boolean;
  onSetState: (state: MigrationState) => void;
}): ReactElement {
  return (
    <div className="flex items-center gap-2">
      {state !== "pending" && (
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => onSetState(state === "done" ? "in_progress" : "pending")}
        >
          {state === "done" ? "Move back to in-progress" : "Move back to pending"}
        </Button>
      )}
      {state !== "done" && (
        <Button
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={() => onSetState("done")}
        >
          {busyState ? "Saving..." : "Mark done"}
        </Button>
      )}
    </div>
  );
}

function RowEditorFields({
  row,
  existingRds,
  form,
  onUpdateForm,
  disabled,
}: RowEditorProps & { disabled: boolean }): ReactElement {
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="flex flex-col gap-1.5">
        <Label className={FIELD_LABEL_CLASS}>RD</Label>
        <RdCombobox
          existingRds={existingRds}
          selectedRdId={form.rdId}
          disabled={disabled}
          onSelect={(rdId: string) => onUpdateForm({ rdId })}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label className={FIELD_LABEL_CLASS}>Client name</Label>
        <Input
          value={form.clientName}
          onChange={(e) => onUpdateForm({ clientName: e.target.value })}
          placeholder="e.g. Joanna"
          disabled={disabled}
          className="rounded-lg border-border"
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label className={FIELD_LABEL_CLASS}>Session date</Label>
        <Input
          type="date"
          value={form.sessionDate}
          onChange={(e) => onUpdateForm({ sessionDate: e.target.value })}
          disabled={disabled}
          className="rounded-lg border-border"
        />
      </div>
      <SessionTypeField
        oldRunId={row.oldRunId}
        value={form.sessionType}
        onChange={(sessionType) => onUpdateForm({ sessionType })}
      />
    </div>
  );
}

function sessionTypeFrom(value: string): FormState["sessionType"] {
  if (value === "initial") return "initial";

  return value === "follow_up" ? "follow_up" : null;
}

function SessionTypeField({
  oldRunId,
  value,
  onChange,
}: {
  oldRunId: string;
  value: FormState["sessionType"];
  onChange: (sessionType: FormState["sessionType"]) => void;
}): ReactElement {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className={FIELD_LABEL_CLASS}>Session type</Label>
      <RadioGroup
        value={value ?? ""}
        onValueChange={(v) => onChange(sessionTypeFrom(v))}
        className="flex items-center gap-4 h-9"
      >
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="initial" id={`type-initial-${oldRunId}`} />
          <Label
            htmlFor={`type-initial-${oldRunId}`}
            className="cursor-pointer text-sm"
          >
            Initial
          </Label>
        </div>
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="follow_up" id={`type-followup-${oldRunId}`} />
          <Label
            htmlFor={`type-followup-${oldRunId}`}
            className="cursor-pointer text-sm"
          >
            Follow-up
          </Label>
        </div>
      </RadioGroup>
    </div>
  );
}
